/**
 * ReviewTable — revisão da seleção antes do upload (RF-M2-007, RN-M2-004):
 * Caminho, Tamanho, SHA-256, Situação (Novo/Duplicado/Fora da carga, com o motivo).
 * ADR-040: a planilha de instalações vai junto e é importada sozinha; o que fica fora diz por quê.
 * ADR-043: qualquer arquivo vai (o tipo é decidido pelo conteúdo); os arquivos de sistema aparecem como ignorados,
 * contados à parte e sem hash (não são enviados).
 * Spec ajustes-telas-amostra (Req 1): a lista fica numa área com rolagem própria e pode ser filtrada pela situação — o
 * filtro só muda o que se vê; o que é enviado continua sendo o conjunto inteiro (a prop `arquivos` não muda).
 */

import { useState } from 'react';
import { ArquivoPreparado, SituacaoArquivo } from '../services/cargas-types';
import { classeSituacao, formatarBytes, rotuloSituacao } from './format';

export interface ReviewTableProps {
  readonly arquivos: readonly ArquivoPreparado[];
}

/** As situações do filtro, na ordem da tela (rótulo no plural). */
const FILTROS: ReadonlyArray<{ readonly situacao: SituacaoArquivo; readonly rotulo: string }> = [
  { situacao: 'novo', rotulo: 'Novos' },
  { situacao: 'duplicado', rotulo: 'Duplicados' },
  { situacao: 'planilha', rotulo: 'Planilhas' },
  { situacao: 'sistema', rotulo: 'Sistema' },
  { situacao: 'invalido', rotulo: 'Fora da carga' },
];

export function ReviewTable({ arquivos }: ReviewTableProps): JSX.Element {
  const [filtro, setFiltro] = useState<SituacaoArquivo | null>(null);
  const contar = (s: SituacaoArquivo): number => arquivos.filter((a) => a.situacao === s).length;
  const novos = contar('novo');
  const duplicados = contar('duplicado');
  const invalidos = contar('invalido');
  const planilhas = contar('planilha');
  const sistema = contar('sistema');
  const partes = [
    `${novos} novo(s)`,
    `${duplicados} duplicado(s)`,
    ...(planilhas > 0 ? [`${planilhas} planilha(s) — importadas automaticamente`] : []),
    ...(sistema > 0 ? [`${sistema} arquivo(s) de sistema — ignorado(s)`] : []),
    `${invalidos} fora da carga`,
  ];
  const visiveis = filtro === null ? arquivos : arquivos.filter((a) => a.situacao === filtro);
  const botao = (rotulo: string, valor: SituacaoArquivo | null, n: number): JSX.Element => (
    <button
      key={valor ?? 'todos'}
      type="button"
      className={filtro === valor ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-ghost'}
      aria-pressed={filtro === valor}
      onClick={() => setFiltro(valor)}
    >
      {`${rotulo} (${n})`}
    </button>
  );
  return (
    <div>
      <p className="muted">{`${arquivos.length} arquivo(s): ${partes.join(', ')}.`}</p>
      <div className="filtro-situacao" role="group" aria-label="Filtrar pela situação">
        {botao('Todos', null, arquivos.length)}
        {FILTROS.filter((f) => contar(f.situacao) > 0).map((f) => botao(f.rotulo, f.situacao, contar(f.situacao)))}
      </div>
      <div className="lista-rolagem">
        <table className="table">
          <thead>
            <tr>
              <th>Caminho</th>
              <th>Tamanho</th>
              <th>SHA-256</th>
              <th>Situação</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((a) => (
              <tr key={a.caminho}>
                <td>{a.caminho}</td>
                <td>{formatarBytes(a.tamanho)}</td>
                <td className="mono">{a.sha256 ? `${a.sha256.slice(0, 12)}…` : '—'}</td>
                <td>
                  <span className={classeSituacao(a.situacao)}>{a.motivo ?? rotuloSituacao(a.situacao)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
